import type { ICommand } from "./ICommand";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "../core/delete";
import { openAs, getEntityHandle, findEntityByHandle } from "../core/oda";
import { OpenMode, Visibility } from "../core/constants";
import { UNDO_LAYER_NAME, ensureUndoLayerExists } from "../database/layers";
import { recordGraphicsModified, getEntityLayerName } from "../entities";
import { HatchReactorManager } from "../managers/HatchReactorManager";
import { oplogEmitter, type OplogEmit } from "../meta/oplog";

export interface DeleteEntityParams {
  /** The OdDbObjectId of the entity */
  entityId: DrawingModule.OdDbObjectId;
  /** The original layer name to restore on undo */
  originalLayerName: string;
}

interface DeleteEntityEntry {
  handle: string;
  originalLayerName: string;
}

/**
 * Command for deleting entities with soft-delete undo.
 * Execute moves entity to hidden layer, undo restores to original layer.
 */
export class DeleteEntityCommand implements ICommand {
  private viewer: DRDrawingViewer;
  private entities: DeleteEntityEntry[];
  private executed = false;
  /** Events oplog émis par cette suppression (pour undo/redo). */
  private oplogEvents: OplogEmit[] = [];

  constructor(viewer: DRDrawingViewer, entities: DeleteEntityParams[]) {
    this.viewer = viewer;
    this.entities = [];

    const reactorManager = HatchReactorManager.getInstance(this.viewer.module);
    const addedHatchHandles = new Set<string>();

    // First, collect handles of explicitly deleted entities to avoid duplicates
    for (const entity of entities) {
      const handle = getEntityHandle(entity.entityId);
      if (handle) {
        addedHatchHandles.add(handle);
      }
    }

    for (const entity of entities) {
      const handle = getEntityHandle(entity.entityId);
      if (handle) {
        this.entities.push({
          handle,
          originalLayerName: entity.originalLayerName,
        });
      }

      // Auto-include block attributes (cascade delete)
      this.collectBlockAttributes(viewer, entity.entityId, addedHatchHandles);

      // Auto-include hatch reactors (cascade delete)
      const hatches = reactorManager.getHatchReactorsForBoundary(
        entity.entityId,
      );
      for (const hatchId of hatches) {
        const hatchHandle = getEntityHandle(hatchId);
        if (hatchHandle && !addedHatchHandles.has(hatchHandle)) {
          addedHatchHandles.add(hatchHandle);
          this.entities.push({
            handle: hatchHandle,
            originalLayerName: getEntityLayerName(viewer.module, hatchId),
          });
        }
      }
    }
  }

  /** Collect attribute entity handles from a BlockReference and add them to the deletion list. */
  private collectBlockAttributes(
    viewer: DRDrawingViewer,
    entityId: DrawingModule.OdDbObjectId,
    addedHandles: Set<string>,
  ): void {
    const module = viewer.module;
    const pObj = entityId.safeOpenObject(OpenMode.kForRead, false);
    const blockRef = module.OdDbBlockReference.cast(pObj);

    if (!blockRef) {
      deleteObj(pObj);
      return;
    }

    const attrIter = blockRef.attributeIterator();
    while (!attrIter.done()) {
      const attrId = attrIter.objectId();
      const attrHandle = getEntityHandle(attrId);
      if (attrHandle && !addedHandles.has(attrHandle)) {
        addedHandles.add(attrHandle);
        this.entities.push({
          handle: attrHandle,
          originalLayerName: getEntityLayerName(module, attrId),
        });
      }
      attrIter.step(true, true);
    }
    deleteObj(attrIter);
    deleteObj(blockRef);
    deleteObj(pObj);
  }

  /** Move entities to hidden undo layer (soft delete) */
  execute(): void {
    if (this.executed) return;

    const db = this.viewer.database;
    if (db) {
      ensureUndoLayerExists(this.viewer.module, db);
    }

    for (const entity of this.entities) {
      const entityId = this.resolveHandle(entity.handle);
      if (!entityId) continue;
      this.setEntityVisibility(entityId, false);
      this.setEntityLayer(entityId, UNDO_LAYER_NAME);
      recordGraphicsModified(this.viewer.module, entityId);
      deleteObj(entityId);
    }

    // Oplog : un event DELETED par entité DR (les entités sont sur UNDO_LAYER mais
    // existent encore → entityUid/defectName lisibles pour le snapshot).
    this.oplogEvents = oplogEmitter.emitForHandles(
      this.viewer,
      "DELETED",
      this.entities.map((e) => e.handle),
    );

    this.executed = true;
  }

  /** Restore entities to original layer */
  undo(): void {
    if (!this.executed) return;

    for (const entity of this.entities) {
      const entityId = this.resolveHandle(entity.handle);
      if (!entityId) continue;
      this.setEntityLayer(entityId, entity.originalLayerName);
      this.setEntityVisibility(entityId, true);
      recordGraphicsModified(this.viewer.module, entityId);
      deleteObj(entityId);
    }

    // Sync hatches after restore (boundaries may have been restored)
    const reactorManager = HatchReactorManager.getInstance(this.viewer.module);
    for (const entity of this.entities) {
      const entityId = this.resolveHandle(entity.handle);
      if (!entityId) continue;
      reactorManager.syncHatchesForBoundary(entityId);
      deleteObj(entityId);
    }

    // Oplog : suppression annulée → on retire les events DELETED.
    oplogEmitter.revert(this.oplogEvents);

    this.executed = false;
  }

  /** Move entities back to hidden undo layer */
  redo(): void {
    if (this.executed) return;

    for (const entity of this.entities) {
      const entityId = this.resolveHandle(entity.handle);
      if (!entityId) continue;
      this.setEntityVisibility(entityId, false);
      this.setEntityLayer(entityId, UNDO_LAYER_NAME);
      recordGraphicsModified(this.viewer.module, entityId);
      deleteObj(entityId);
    }

    // Oplog : re-suppression → on ré-ajoute les events DELETED.
    oplogEmitter.reemit(this.oplogEvents);

    this.executed = true;
  }

  /** Clean up - permanently delete if in undo layer */
  dispose(): void {
    // delete entities from undo layer only if command was executed
    if (this.executed) {
      for (const entityParams of this.entities) {
        const entityId = this.resolveHandle(entityParams.handle);
        if (!entityId) continue;

        const entity = this.openEntity(entityId, OpenMode.kForWrite);
        deleteObj(entityId);
        if (!entity) continue;

        const currentLayer = entity.layer().toString();
        if (currentLayer === UNDO_LAYER_NAME) {
          entity.erase(true);
        }
        deleteObj(entity);
      }
    }
  }

  private resolveHandle(handle: string): DrawingModule.OdDbObjectId | null {
    return findEntityByHandle(this.viewer.module, this.viewer.database, handle);
  }

  private setEntityLayer(
    entityId: DrawingModule.OdDbObjectId,
    layerName: string,
  ): void {
    const entity = this.openEntity(entityId, OpenMode.kForWrite);
    if (!entity) return;

    try {
      const module = this.viewer.module;
      const layerStr = new module.OdString();
      layerStr.set(layerName);
      entity.setLayer(layerStr, false, true);
      deleteObj(layerStr);
    } finally {
      deleteObj(entity);
    }
  }

  private setEntityVisibility(
    entityId: DrawingModule.OdDbObjectId,
    visible: boolean,
  ): void {
    const entity = this.openEntity(entityId, OpenMode.kForWrite);
    if (!entity) return;
    try {
      entity.setVisibility(
        visible ? Visibility.kVisible : Visibility.kInvisible,
        false,
      );
    } finally {
      deleteObj(entity);
    }
  }

  private openEntity(
    entityId: DrawingModule.OdDbObjectId,
    openMode: DrawingModule.OpenMode,
  ): DrawingModule.OdDbEntity | null {
    return openAs(entityId, this.viewer.module.OdDbEntity, openMode);
  }
}
